import Service, { inject as service } from "@ember/service"
import { createDocumentStore, type DocumentStore } from "@supergrain/silo"

import { migrateFromCardsInPlace } from "@shared/lib/card-stack/unified-migration"
import type { PatchableDocument, PatchModel } from "@shared/models/patch"
import type { TypeToModel } from "@shared/models/type-to-model"
import {
  createInsertContext,
  type DocumentInsertContext,
  type DocumentInsertedListener,
  type JsonApiDocument,
} from "../lib/document-backends/types"
import { stripPrototypesInPlace } from "@shared/lib/strip-prototypes"
import { isPatchableRecord, patchSupergrainDocument } from "../lib/supergrain-patch"
import {
  createSupergrainModelConfigs,
  hasSupergrainModelConfig,
} from "../lib/supergrain/adapters/model-configs"
import type Ajax from "./ajax"
import type PatchCoordinator from "./patch-coordinator"
import type SocketService from "./socket"

type ModelType = keyof TypeToModel & string
type SupergrainDocument = TypeToModel[ModelType]

/**
 * The internal Supergrain-side store. It owns the `@supergrain/silo` document
 * store and is the backend that the public `react-store` facade reads from and
 * writes to. Application components should talk to the `react-store` facade, not
 * this service; the document replicator and patch coordinator keep it in sync
 * with the internal Ember store.
 */
export default class InternalSupergrainStore extends Service {
  @service declare ajax: Ajax
  @service declare patchCoordinator: PatchCoordinator
  @service declare socket: SocketService

  private _documentStore: DocumentStore<TypeToModel> | null = null
  private _modelConfigs: ReturnType<typeof createSupergrainModelConfigs> | null = null
  private readonly activeInsertContexts: DocumentInsertContext[] = []
  private readonly afterInsertListeners = new Set<DocumentInsertedListener>()

  private get modelConfigs(): ReturnType<typeof createSupergrainModelConfigs> {
    this._modelConfigs ??= createSupergrainModelConfigs(this.ajax, (type, id) =>
      this.socket.subscribe(type, id)
    )
    return this._modelConfigs
  }

  get documentStore(): DocumentStore<TypeToModel> {
    this._documentStore ??= createDocumentStore<TypeToModel>({
      hooks: {
        prepareInsert: (type, doc) => {
          // Model-specific migrations. Mirrored in InternalEmberStore.insertDocument.
          if (isCardStack(doc)) migrateFromCardsInPlace(doc)

          // Add frontend-only meta field.
          doc.meta ??= {}

          // Stored documents are prototype-free at every level (see
          // lib/strip-prototypes.ts for the full invariant). Every insert path
          // (replication, patch-create, adapter loads, sideloads) funnels
          // through this hook, so this is the single insert-side enforcement
          // point. Must be in-place: patch() keeps mutating its doc reference
          // after inserting.
          stripPrototypesInPlace(doc)

          // Skip overwriting a newer revision.
          const existing = this._documentStore?.findInMemory(type, doc.id)
          if (existing) {
            const existingRev = revisionFromDocument(existing)
            const docRev = revisionFromDocument(doc)
            if (
              typeof existingRev === "number" &&
              typeof docRev === "number" &&
              existingRev > docRev
            ) {
              return null
            }
          }

          return doc
        },

        afterInsert: (_type, doc) => {
          const context =
            this.activeInsertContexts[this.activeInsertContexts.length - 1] ??
            createInsertContext("backend-load")
          this.emitAfterInsert(asJsonApiDocument(doc), context)
        },
      },

      models: this.modelConfigs,
    })

    return this._documentStore
  }

  hasDocument(type: string, id: string): boolean {
    if (!this.canStore(type)) return false
    return !!this.documentStore.findInMemory(type, id)
  }

  getRevision(type: string, id: string): number {
    const doc = this.findInMemory(type, id)
    const revision = revisionFromDocument(doc)
    return typeof revision === "number" ? revision : 0
  }

  findInMemory(type: string, id: string): TypeToModel[ModelType] | undefined {
    if (!this.canStore(type)) return undefined
    return this.documentStore.findInMemory(type, id)
  }

  patch(patch: PatchModel): (() => void) | null {
    const type = patch.document.modelType
    if (!this.canStore(type)) return null

    let doc = this.documentStore.findInMemory(type, patch.document.id)

    if (doc == null && patch.isCreatePatch !== true) return null

    if (doc == null) {
      doc = createEmptyDocument(type, patch.document.id)
      this.insertSupergrainDocument(type, doc, createInsertContext("patch-create"))
    }

    doc.meta ??= Object.create(null)
    return patchSupergrainDocument(asPatchableDocument(doc), patch)
  }

  insertDocument(
    doc: JsonApiDocument,
    context: DocumentInsertContext = createInsertContext("manual")
  ): void {
    if (!this.canStore(doc.type)) return

    const supergrainDoc = toSupergrainDocument(doc)
    this.insertSupergrainDocument(doc.type, supergrainDoc, context)

    if (context.origin !== "replication") {
      // If we're overwriting an existing doc (e.g. from checkStaleness), we don't
      // know which optimistic local updates are included in the server's new version.
      // It seems safest, and reasonably likely, to assume that all of them are included.
      this.patchCoordinator.clearQueue(doc.type, doc.id)
    }
  }

  onAfterInsert(listener: DocumentInsertedListener): () => void {
    this.afterInsertListeners.add(listener)
    return () => this.afterInsertListeners.delete(listener)
  }

  canStore(type: string): type is ModelType {
    return hasSupergrainModelConfig(this.modelConfigs, type)
  }

  private emitAfterInsert(doc: JsonApiDocument, context: DocumentInsertContext): void {
    for (const listener of this.afterInsertListeners) {
      listener({ backendId: "supergrain", doc, context })
    }
  }

  private insertSupergrainDocument(
    type: ModelType,
    doc: SupergrainDocument,
    context: DocumentInsertContext
  ): void {
    this.activeInsertContexts.push(context)

    try {
      this.documentStore.insertDocument(type, doc)
    } finally {
      this.activeInsertContexts.pop()
    }
  }
}

function createEmptyDocument(type: ModelType, id: string): SupergrainDocument {
  return toSupergrainDocument({
    type,
    id,
    attributes: {},
    meta: {},
  })
}

function isCardStack(doc: SupergrainDocument): doc is TypeToModel["card-stack"] {
  return "type" in doc && doc.type === "card-stack"
}

function toSupergrainDocument(doc: JsonApiDocument): SupergrainDocument {
  return doc as SupergrainDocument
}

function asJsonApiDocument(doc: SupergrainDocument): JsonApiDocument {
  return doc as JsonApiDocument
}

function asPatchableDocument(doc: SupergrainDocument): PatchableDocument {
  if (!isPatchableRecord(doc)) {
    throw new Error("Cannot patch non-object Supergrain document")
  }

  return doc
}

function revisionFromDocument(doc: unknown): number | undefined {
  if (!isRecord(doc) || !("attributes" in doc)) return undefined

  const { attributes } = doc
  if (!isRecord(attributes) || !("_revision" in attributes)) return undefined

  const { _revision } = attributes
  return typeof _revision === "number" ? _revision : undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}
declare module "@ember/service" {
  interface Registry {
    "internal-supergrain-store": InternalSupergrainStore
  }
}
